import { FC, useContext, useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { ClockIcon, Paragraph, Tooltip } from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';
import { convertToHours } from 'utils';

import { SettingsContext } from '../../settings';
import { ITimerProps } from './interfaces';
import s from './SessionRestTimer.module.scss';

export const SessionRestTimer: FC<ITimerProps> = ({
  rest,
  sessionId,
  before5MinutesPlaceholder,
  showDot,
  className,
  postpaid,
  total_cost,
  showMinimal,
}) => {
  const [seconds, setSeconds] = useState(rest ?? 0);
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const apolloClient = useApolloClient();

  useEffect(() => {
    if (rest) {
      setSeconds(rest);
    }
  }, [rest]);

  useEffect(() => {
    const id = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);

        if (sessionId) {
          apolloClient.cache.modify({
            id: apolloClient.cache.identify({
              id: sessionId,
              __typename: 'ClientSessionOverview',
            }),
            fields: {
              time_left: (value) => {
                if (value) {
                  return value - 1;
                }
              },
            },
            broadcast: true,
          });
        }

        return;
      }

      apolloClient.cache.evict({
        id: apolloClient.cache.identify({
          id: sessionId,
          __typename: 'ClientSessionOverview',
        }),
      });
      apolloClient.cache.gc();
    }, 1000);

    return () => {
      clearInterval(id);
    };
  }, [seconds]);

  if (before5MinutesPlaceholder && seconds > 5 * 60)
    return (
      <span className={s.mobile}>
        <ClockIcon size="s" />
        {before5MinutesPlaceholder}
      </span>
    );

  if (postpaid)
    return (
      <Tooltip
        className={s.mobile}
        contentClassName={s.tooltip_content}
        content={
          <div className={s.tooltip}>
            <div className={s.tooltip__row}>
              <Paragraph size="s">{t('duration')}</Paragraph>
              <Paragraph size="s">
                {rest ? convertToHours(seconds) : ''}
              </Paragraph>
            </div>
            <div className={s.tooltip__row}>
              <Paragraph size="s">{t('postPayment.total_cost')}</Paragraph>
              <Paragraph size="s">{`${total_cost ?? 0} ${
                currency.sign
              }`}</Paragraph>
            </div>
          </div>
        }
      >
        <span className={cn(s.post_payments, s.mobile)}>
          {showDot && <span className={s.dot}>•</span>}
          <span className={s.duration}>
            {rest ? convertToHours(seconds, showMinimal) : ''}
          </span>
        </span>
      </Tooltip>
    );

  return (
    <span className={cn(className, showMinimal && s.mobile)}>
      {showDot && <span className={s.dot}>•</span>}
      <span className={cn(showDot && s.duration)}>
        {rest ? convertToHours(seconds, showMinimal) : ''}
      </span>
    </span>
  );
};
